import React, { useEffect, useRef, useState } from "react";
import { useSelector, useStore } from "react-redux";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import SelectionDropdown from "./SelectionDropdown";
import "./CropwiseData/CropwiseData.css";
import "./DEMView.css";
import ProgressBar from "./Loader/ProgressBar";
import Spinner from "./Loader/Spinner";
import { GEO_SERVER_BASE_URL } from "../config/constants";

// Loading state constants
const LOADING_STATES = {
  IDLE: 'idle',
  LOCATION_SEARCH: 'location_search',
  DEM_LOADING: 'dem_loading',
  LULC_LOADING: 'lulc_loading',
  KHASRA_LOADING: 'khasra_loading'
};

// 🚀 PATCH: Fix Leaflet race condition where _tileReady crashes if layer is removed
// "Cannot read properties of null (reading '_fadeAnimated')"
// Also prevents stack overflow on hot reloads
if (!L.GridLayer.prototype._tileReady.isPatched) {
  const originalTileReady = L.GridLayer.prototype._tileReady;
  L.GridLayer.include({
    _tileReady: function (coords, err, tile) {
      if (!this._map || !tile) return;
      try {
        return originalTileReady.call(this, coords, err, tile);
      } catch (e) {
        if (e.message && e.message.includes('_fadeAnimated')) {
          return;
        }
        console.warn("Leaflet _tileReady error suppressed:", e);
      }
    }
  });
  L.GridLayer.prototype._tileReady.isPatched = true;
}

const extractProp = (props, ...keys) => {
  for (const k of keys) {
    if (props[k] !== undefined && props[k] !== null) return props[k];
  }
  return null;
};
const canonicalName = (name) => name?.toLowerCase().trim().replace(/[^a-z0-9]/g, "") || "";
const prettyName = (name) => name || "";

const safeFitBounds = (map, layer) => {
  if (!map || !layer) return;
  try {
    const bounds = layer.getBounds();
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  } catch (err) {
    console.warn("safeFitBounds error:", err);
  }
};

const DigitalElevationView = () => {
  const hasShownDEMErrorRef = useRef(false);
  const hasShownKhasraErrorRef = useRef(false);


  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Loading states
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('');

  const currentPolygonLayer = useRef(null);
  const khasraHighlightRef = useRef(null);
  const hasShownKhasraTileErrorRef = useRef(false);

  let khasraClickHandlerBound = false;
  const layerControlRef = useRef(null);
  const demLayerRef = useRef(null);
  const khasraLayerRef = useRef(null);

  // Layer states
  // Layer states
  const [areLayersHidden, setAreLayersHidden] = useState(false);
  const [isSatelliteVisible, setIsSatelliteVisible] = useState(true);
  const satelliteLayerRef = useRef(null);
  const openStreetLayerRef = useRef(null);

  // Redux State
  const {
    state: selectedState,
    district: selectedDistrict,
    tehsil: selectedTehsil,
    village: selectedVillage
  } = useSelector((state) => state.location);

  const { geojson } = useSelector((state) => state.geo);
  const store = useStore(); // Direct store access for Condition 2

  // LULC states
  const [showLULC, setShowLULC] = useState(false);
  const [selectedTehsilForLULC, setSelectedTehsilForLULC] = useState(null);
  const lulcLayerRef = useRef(null);
  const [lulcData, setLulcData] = useState(null);
  const [lulcLayerLoading, setLulcLayerLoading] = useState(false);

  const hybridLayerRef = useRef(null);

  // Map Layer Refs (State managed by map updates)
  const [districtLayer, setDistrictLayer] = useState(null);
  const [tehsilLayer, setTehsilLayer] = useState(null);
  const [geoJsonLayer, setGeoJsonLayer] = useState(null);

  const navigate = useNavigate();

  // DEM states
  const [showDEM, setShowDEM] = useState(true);
  const [demLayerLoading, setDemLayerLoading] = useState(false);
  const [showKhasra, setShowKhasra] = useState(false);

  // Get loading message based on state and progress
  const getLoadingMessage = (state, progress) => {
    switch (state) {
      case LOADING_STATES.LOCATION_SEARCH:
        if (progress < 40) return "Searching location...";
        if (progress < 80) return "Loading boundaries...";
        return "Finalizing map view...";
      case LOADING_STATES.DEM_LOADING:
        if (progress < 30) return "Fetching elevation data...";
        if (progress < 70) return "Processing DEM layer...";
        return "Rendering elevation model...";
      case LOADING_STATES.LULC_LOADING:
        if (progress < 30) return "Loading land use data...";
        if (progress < 70) return "Processing LULC layer...";
        return "Rendering land cover...";
      case LOADING_STATES.KHASRA_LOADING:
        if (progress < 50) return "Loading field boundaries...";
        return "Preparing parcel data...";
      default:
        return "Loading...";
    }
  };

  // ✅ DEM Layers for States
  const demLayers = {
    Rajasthan: "ne:Raj_12",
    Maharashtra: "ne:Maharashtra_DEM",
    "Uttar Pradesh": "ne:UP_DEM",
  };

  const baseLayers = {
    OpenStreetMap: L.tileLayer(
      "http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution: "© OpenStreetMap contributors",
      }
    ),
    Satellite: L.tileLayer(
      "http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      {
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        attribution: "© Google Maps",
      }
    ),
    Hybrid: L.tileLayer("http://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", {
      subdomains: ["mt0", "mt1", "mt2", "mt3"],
      attribution: "© Google Maps",
    }),
  };

  useEffect(() => {
    if (!mapInstanceRef.current) {
      // 🚀 Fix: Disable fadeAnimation to prevent 'Cannot read properties of null (reading _fadeAnimated)' error
      const map = L.map(mapRef.current, {
        fadeAnimation: false,
        zoomAnimation: false,
        markerZoomAnimation: false
      }).setView([20, 78], 5);
      mapInstanceRef.current = map;

      // Store references to base layers
      satelliteLayerRef.current = baseLayers["Satellite"];
      openStreetLayerRef.current = baseLayers["OpenStreetMap"];
      hybridLayerRef.current = baseLayers["Hybrid"];

      // ✅ Add OpenStreetMap by default
      satelliteLayerRef.current.addTo(map);

      map.createPane("tehsilPane");
      map.getPane("tehsilPane").style.zIndex = 300;

      map.createPane("districtPane");
      map.getPane("districtPane").style.zIndex = 200;

      map.createPane("khasraPane");
      map.getPane("khasraPane").style.zIndex = 450;

      // Add layer control
      layerControlRef.current = L.control.layers(baseLayers, {}).addTo(map);

      // ✅ Condition 2: After map loads, wait for cached thunks to resolve, then zoom
      setTimeout(() => {
        const { location, geo } = store.getState();
        if (location.village && geo.geojson.village && mapInstanceRef.current) {
          console.log("🔄 [Condition 2] Zooming to:", location.village);
          handleSearch(location.state, location.district, location.tehsil, location.village, geo.geojson.village);
        }
      }, 300);
    }
  }, []);

  // Disable interaction from vector layers when Khasra is shown
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const districtPane = map.getPane("districtPane");
    const tehsilPane = map.getPane("tehsilPane");

    if (districtPane)
      districtPane.style.pointerEvents = showKhasra ? "none" : "auto";
    if (tehsilPane)
      tehsilPane.style.pointerEvents = showKhasra ? "none" : "auto";
  }, [showKhasra]);

  const handleHideLayers = () => {
    if (areLayersHidden) {
      // Show all layers again
      if (districtLayer) districtLayer.setStyle({ opacity: 1 });
      if (tehsilLayer) tehsilLayer.setStyle({ opacity: 1 });
      if (geoJsonLayer) geoJsonLayer.setStyle({ opacity: 1 });
    } else {
      // Hide all layers
      if (districtLayer) districtLayer.setStyle({ opacity: 0 });
      if (tehsilLayer) tehsilLayer.setStyle({ opacity: 0 });
      if (geoJsonLayer) geoJsonLayer.setStyle({ opacity: 0 });
    }

    setAreLayersHidden(!areLayersHidden);
  };

  const handleSearch = async (
    state,
    district,
    tehsil,
    village,
    geoJsonData
  ) => {
    if (!mapInstanceRef.current) return;

    if (!geoJsonData || !geoJsonData.features) {
      console.warn("Invalid GeoJSON data.");
      return;
    }

    // Update Redux state props if needed locally, but we rely on args
    if (tehsil) setSelectedTehsilForLULC(tehsil);

    try {
      setLoadingState(LOADING_STATES.LOCATION_SEARCH);
      setProgress(20);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 20));

      let currentDistrictLayer = districtLayer;
      let currentTehsilLayer = tehsilLayer;
      let currentVillageLayer = geoJsonLayer;

      // --- DISTRICT LOGIC ---
      if (district && !tehsil) {
        // Check if we need to recreate/update district layer
        // Logic: if district changed or layer missing

        // For simplicity/robustness similar to CHM:
        // If we have a district layer, we might want to refresh it if the state changed, 
        // but usually SelectionDropdown handles state change by re-fetching.

        // We'll create it if it doesn't exist, styling it for the selected district.
        if (currentDistrictLayer) {
          mapInstanceRef.current.removeLayer(currentDistrictLayer);
        }

        const newDistrictLayer = L.geoJSON(geoJsonData, {
          style: function (feature) {
            const rawName = extractProp(feature.properties, "district", "District", "NAME_2");
            const isSelected = canonicalName(rawName) === canonicalName(district);
            return {
              color: "red",
              weight: 2,
              fillOpacity: 0,
            };
          },
          onEachFeature: function (feature, layer) {
            const districtName = extractProp(feature.properties, "District", "district", "NAME_2");
            layer.bindTooltip(districtName, {
              permanent: true,
              direction: "center",
              className: "district-label",
            });
            if (canonicalName(districtName) === canonicalName(district)) {
              layer.bindPopup(`<b>Selected District:</b> ${districtName}`).openPopup();
            }
          },
        }).addTo(mapInstanceRef.current);

        setDistrictLayer(newDistrictLayer);
        currentDistrictLayer = newDistrictLayer;

        // Fit bounds to selected district if no tehsil/village
        if (!tehsil && !village) {
          const selectedLayer = newDistrictLayer.getLayers().find(l => {
            const name = extractProp(l.feature.properties, "District", "district", "NAME_2");
            return canonicalName(name) === canonicalName(district);
          });
          if (selectedLayer) {
            safeFitBounds(mapInstanceRef.current, selectedLayer);
          } else {
            safeFitBounds(mapInstanceRef.current, newDistrictLayer);
          }
        }

      } else if (state && !district) {
        // STATE VIEW
        if (currentDistrictLayer) mapInstanceRef.current.removeLayer(currentDistrictLayer);
        if (tehsilLayer) {
          mapInstanceRef.current.removeLayer(tehsilLayer);
          setTehsilLayer(null);
        }
        if (geoJsonLayer) {
          mapInstanceRef.current.removeLayer(geoJsonLayer);
          setGeoJsonLayer(null);
        }

        const newStateLayer = L.geoJSON(geoJsonData, {
          style: { color: "red", weight: 2, fillOpacity: 0 },
          onEachFeature: function (feature, layer) {
            const dName = extractProp(feature.properties, "District", "district", "NAME_2");
            layer.bindTooltip(dName, {
              permanent: true,
              direction: "center",
              className: "district-label",
            });
            setTimeout(() => {
              const el = layer.getElement();
              if (el) el.style.pointerEvents = "none";
            }, 100);
          }
        }).addTo(mapInstanceRef.current);

        setDistrictLayer(newStateLayer);
        currentDistrictLayer = newStateLayer;
        safeFitBounds(mapInstanceRef.current, newStateLayer);
      }

      // --- TEHSIL LOGIC ---
      if (tehsil) {
        // CHMPage Logic: Render ALL tehsils if tehsil selected (Red/Orange)
        // But only if NO village selected (to avoid clutter)
        if (geoJsonData.features && !village) {
          // ✅ Fix: Hide District layer when Tehsil is selected to prevent label overlap
          if (currentDistrictLayer && mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
            mapInstanceRef.current.removeLayer(currentDistrictLayer);
          }

          if (currentTehsilLayer) {
            mapInstanceRef.current.removeLayer(currentTehsilLayer);
          }

          const getTehsilName = (props) => extractProp(props, "tehsil", "Tehsil", "subdist", "NAME_3", "NAME", "Tehsil_Name", "tehsil_name", "TEHSIL");

          const newTehsilLayer = L.geoJSON({ ...geoJsonData }, {
            style: function (feature) {
              const tName = getTehsilName(feature.properties);
              const isSelected = canonicalName(tName) === canonicalName(tehsil);
              return {
                color: isSelected ? "#DC2626" : "#FB923C",
                weight: isSelected ? 3 : 1,
                fillOpacity: isSelected ? 0.1 : 0,
                fillColor: isSelected ? "#DC2626" : "transparent"
              };
            },
            onEachFeature: function (feature, layer) {
              const tName = getTehsilName(feature.properties);
              const isSelected = canonicalName(tName) === canonicalName(tehsil);

              if (tName) {
                layer.bindTooltip(tName, {
                  permanent: true,
                  direction: "center",
                  className: isSelected ? "tehsil-label tehsil-label-selected" : "tehsil-label",
                });
                if (isSelected) {
                  layer.bindPopup(`<b>Tehsil:</b> ${tName}`).openPopup();
                }
              }
              setTimeout(() => {
                const el = layer.getElement();
                if (el) el.style.pointerEvents = "none";
              }, 100);
            }
          }).addTo(mapInstanceRef.current);

          setTehsilLayer(newTehsilLayer);
          currentTehsilLayer = newTehsilLayer;

          // Zoom to selected tehsil
          const selectedFeatures = geoJsonData.features.filter(f => canonicalName(getTehsilName(f.properties)) === canonicalName(tehsil));
          if (selectedFeatures.length > 0) {
            const temp = L.geoJSON({ type: "FeatureCollection", features: selectedFeatures });
            safeFitBounds(mapInstanceRef.current, temp);
          } else {
            safeFitBounds(mapInstanceRef.current, newTehsilLayer);
          }
        } else {
          // If village selected, hide Tehsil layer logic
          if (currentTehsilLayer) {
            if (village) {
              if (mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
                mapInstanceRef.current.removeLayer(currentTehsilLayer);
              }
            } else {
              if (!mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
                currentTehsilLayer.addTo(mapInstanceRef.current);
              }
              currentTehsilLayer.setStyle({ opacity: 1 });
            }
          }
        }
      }

      // --- VILLAGE LOGIC ---
      if (village) {
        if (currentVillageLayer) {
          mapInstanceRef.current.removeLayer(currentVillageLayer);
        }

        const newVillageLayer = L.geoJSON(geoJsonData, {
          style: function (feature) {
            const rawName = extractProp(feature.properties, "village", "Village", "NAME_4", "NAME", "Name");
            const isSelected = canonicalName(rawName) === canonicalName(village);
            return {
              color: isSelected ? "#7C3AED" : "#F97316",
              weight: isSelected ? 3 : 1,
              fillOpacity: isSelected ? 0.15 : 0,
              fillColor: isSelected ? "#7C3AED" : "transparent"
            };
          },
          onEachFeature: function (feature, layer) {
            const rawName = extractProp(feature.properties, "village", "Village", "NAME_4", "NAME", "Name");
            if (!rawName) return;
            const displayName = prettyName(rawName);
            const isSelected = canonicalName(rawName) === canonicalName(village);

            layer.bindTooltip(displayName, {
              permanent: true,
              direction: "center",
              className: isSelected ? "village-label village-label-selected" : "village-label"
            });
            if (isSelected) {
              layer.bindPopup(`<b>Village:</b> ${displayName}`).openPopup();
            }
            setTimeout(() => {
              const el = layer.getElement();
              if (el) el.style.pointerEvents = "none";
            }, 100);
          }
        }).addTo(mapInstanceRef.current);

        setGeoJsonLayer(newVillageLayer);

        const selectedFeatures = geoJsonData.features.filter(f => {
          const rawName = extractProp(f.properties, "village", "Village", "NAME_4", "NAME", "Name");
          return canonicalName(rawName) === canonicalName(village);
        });

        if (selectedFeatures.length > 0) {
          const temp = L.geoJSON({ type: "FeatureCollection", features: selectedFeatures });
          safeFitBounds(mapInstanceRef.current, temp);
        } else {
          safeFitBounds(mapInstanceRef.current, newVillageLayer);
        }

        // HIDE parent layers
        if (currentTehsilLayer && mapInstanceRef.current.hasLayer(currentTehsilLayer)) {
          mapInstanceRef.current.removeLayer(currentTehsilLayer);
        }
        if (currentDistrictLayer && mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
          mapInstanceRef.current.removeLayer(currentDistrictLayer);
        }

      } else {
        // No village selected
        if (currentVillageLayer) {
          mapInstanceRef.current.removeLayer(currentVillageLayer);
          setGeoJsonLayer(null);
        }
        // Restore parent layers
        if (currentTehsilLayer && !mapInstanceRef.current.hasLayer(currentTehsilLayer) && tehsil) {
          currentTehsilLayer.addTo(mapInstanceRef.current);
          safeFitBounds(mapInstanceRef.current, currentTehsilLayer);
        }
        if (currentDistrictLayer && !mapInstanceRef.current.hasLayer(currentDistrictLayer)) {
          currentDistrictLayer.addTo(mapInstanceRef.current);
          if (tehsil) currentDistrictLayer.setStyle({ opacity: 0.3 });
        }
      }

      setProgress(100);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LOCATION_SEARCH, 100));

    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setTimeout(() => {
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
      }, 500);
    }
  };






  const toggleLULCLayer = async () => {
    if (!selectedTehsilForLULC) {
      console.error("No tehsil selected for LU/LC layer.");
      return;
    }

    const lulcLayerName = `ne:${selectedTehsilForLULC}_LULC`;

    if (showLULC) {
      if (lulcLayerRef.current) {
        mapInstanceRef.current.removeLayer(lulcLayerRef.current);
        lulcLayerRef.current = null;
      }
      setShowLULC(false);
      setLulcLayerLoading(false);
      return;
    }

    // Start LULC loading
    setLoadingState(LOADING_STATES.LULC_LOADING);
    setLulcLayerLoading(true);
    setProgress(10);
    setLoadingMessage(getLoadingMessage(LOADING_STATES.LULC_LOADING, 10));

    try {
      setProgress(30);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LULC_LOADING, 30));

      const lulcLayer = L.tileLayer.wms(
        `${GEO_SERVER_BASE_URL}/wms`,
        {
          layers: lulcLayerName,
          format: "image/png",
          transparent: true,
          version: "1.1.0",
          attribution: "GeoServer",
        }
      );

      setProgress(60);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LULC_LOADING, 60));

      // A WMS layer consists of several tile requests. One tile can fail while
      // the rest still load, so do not treat an individual tileerror as a
      // failure of the entire LU/LC layer.
      let loadedTileCount = 0;
      let failedTileCount = 0;

      lulcLayer.on('loading', () => {
        setProgress(70);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.LULC_LOADING, 70));
      });

      const handleTileLoad = () => {
        loadedTileCount += 1;
      };

      const handleTileError = (error) => {
        failedTileCount += 1;
        console.warn('LU/LC tile failed to load:', error);
      };

      const handleInitialLoad = () => {
        lulcLayer.off('tileload', handleTileLoad);
        lulcLayer.off('tileerror', handleTileError);

        if (loadedTileCount === 0 && failedTileCount > 0) {
          console.error('LU/LC layer failed: all requested tiles returned errors.');
          if (mapInstanceRef.current?.hasLayer(lulcLayer)) {
            mapInstanceRef.current.removeLayer(lulcLayer);
          }
          if (lulcLayerRef.current === lulcLayer) {
            lulcLayerRef.current = null;
          }
          setShowLULC(false);
          setLoadingState(LOADING_STATES.IDLE);
          setLulcLayerLoading(false);
          setProgress(0);
          setLoadingMessage('');
          alert('Failed to load LU/LC layer. Please try again.');
          return;
        }

        setProgress(100);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.LULC_LOADING, 100));

        setTimeout(() => {
          setLoadingState(LOADING_STATES.IDLE);
          setLulcLayerLoading(false);
          setProgress(0);
          setLoadingMessage('');
        }, 500);
      };

      lulcLayer.on('tileload', handleTileLoad);
      lulcLayer.on('tileerror', handleTileError);
      lulcLayer.once('load', handleInitialLoad);

      lulcLayer.addTo(mapInstanceRef.current);
      lulcLayerRef.current = lulcLayer;
      lulcLayerRef.current.setZIndex(500);
      setShowLULC(true);

      setProgress(90);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.LULC_LOADING, 90));

    } catch (error) {
      console.error('Error loading LULC layer:', error);
      setLoadingState(LOADING_STATES.IDLE);
      setLulcLayerLoading(false);
      setProgress(0);
      setLoadingMessage('');
      alert('Failed to load LULC layer. Please check your connection and try again.');
    }
  };

  // Legend effect
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Remove Existing Legend
    const existingLegends = document.querySelectorAll(".info.legend");
    existingLegends.forEach((legend) => legend.remove());

    const legend = L.control({ position: "bottomright" });

    legend.onAdd = function () {
      const div = L.DomUtil.create("div", "info legend");
      div.style.marginBottom = "2px";
      div.style.background = "#212529";
      div.style.padding = "5px";
      div.style.borderRadius = "8px";
      div.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
      div.style.maxHeight = "220px";
      div.style.maxWidth = "180px";
      div.style.marginRight = "0px";
      div.style.overflowY = "auto";
      div.style.fontFamily = "Arial, sans-serif";

      // ✅ LU/LC Legend
      div.innerHTML += `<h4 style="text-align: center; font-weight: bold; margin-bottom: 8px;">LU/LC Classification</h4>`;
      const lulcCategories = [
        { color: "#00ff00", label: "Agriculture" },
        { color: "#808080", label: "Road" },
        { color: "#0000ff", label: "Water" },
        { color: "#ff0000", label: "Settlement" },
        { color: "#ffff00", label: "Other" },
      ];
      lulcCategories.forEach(({ color, label }) => {
        div.innerHTML += `<div style="display: flex; align-items: center; margin-bottom: 6px;">
                    <div style="width: 18px; height: 10px; background: ${color}; margin-right: 6px;"></div>
                    <span style="font-size: 11px;">${label}</span>
                </div>`;
      });

      div.innerHTML += `<hr style="margin: 8px 0; border-top: 1px solid #ccc;">`;

      // ✅ DEM Legend
      div.innerHTML += `<h4 style="text-align: center; font-weight: bold; margin-bottom: 8px;">DEM Legend</h4>`;
      const demCategories = [
        { color: "#2eecd6", label: "<100 (Flood Plain)" },
        { color: "#ffe4c1", label: "100-200 (Pediplain)" },
        { color: "#012c11", label: "200-300 (Pedimount)" },
        { color: "#f1ba4d", label: "300-500 (Plateau)" },
        { color: "#210712", label: "500-700 (Hills)" },
        { color: "#0010d4", label: "700-1701 (Mountain)" },
      ];
      demCategories.forEach(({ color, label }) => {
        div.innerHTML += `<div style="display: flex; align-items: center; margin-bottom: 6px;">
                    <div style="width: 18px; height: 10px; background: ${color}; margin-right: 6px;"></div>
                    <span style="font-size: 11px;">${label}</span>
                </div>`;
      });

      return div;
    };

    legend.addTo(mapInstanceRef.current);
  }, [selectedState]);

  // DEM loading effect
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    if (!selectedState) return;

    const loadDEM = async () => {
      setLoadingState(LOADING_STATES.DEM_LOADING);
      setDemLayerLoading(true);
      setProgress(10);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 10));

      try {
        if (demLayerRef.current) {
          mapInstanceRef.current.removeLayer(demLayerRef.current);
          if (layerControlRef.current) {
            layerControlRef.current.removeLayer(demLayerRef.current);
          }
        }

        setProgress(30);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 30));

        const wmsUrl = `${GEO_SERVER_BASE_URL}/wms`;
        const layerName = demLayers[selectedState];

        if (!layerName) {
          console.error("DEM data not available for the selected state.");
          setLoadingState(LOADING_STATES.IDLE);
          setDemLayerLoading(false);
          setProgress(0);
          setLoadingMessage('');
          return;
        }

        setProgress(60);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 60));

        demLayerRef.current = L.tileLayer.wms(wmsUrl, {
          layers: layerName,
          format: "image/png",
          transparent: true,
          version: "1.1.0",
          attribution: "GeoServer",
        });

        // Add loading event handlers
        demLayerRef.current.on('loading', () => {
          setProgress(70);
          setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 70));
        });

        demLayerRef.current.on('load', () => {
          setProgress(100);
          setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 100));

          setTimeout(() => {
            setLoadingState(LOADING_STATES.IDLE);
            setDemLayerLoading(false);
            setProgress(0);
            setLoadingMessage('');
          }, 500);
        });

        demLayerRef.current.on('tileerror', (error) => {
          console.error('DEM layer loading error:', error);
          setLoadingState(LOADING_STATES.IDLE);
          setDemLayerLoading(false);
          setProgress(0);
          setLoadingMessage('');
          if (!hasShownDEMErrorRef.current) {
            alert('Failed to load DEM layer. Please try again.');
            hasShownDEMErrorRef.current = true;

            setTimeout(() => {
              hasShownDEMErrorRef.current = false;
            }, 10000); // Reset in 10 seconds
          }

        });

        if (showDEM) {
          demLayerRef.current.addTo(mapInstanceRef.current);
          ensureKhasraOnTop();
        }

        if (layerControlRef.current) {
          layerControlRef.current.addOverlay(demLayerRef.current, "DEM Layer");
        }

        setProgress(90);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 90));

      } catch (error) {
        console.error('Error loading DEM layer:', error);
        setLoadingState(LOADING_STATES.IDLE);
        setDemLayerLoading(false);
        setProgress(0);
        setLoadingMessage('');
        alert('Failed to load DEM layer. Please check your connection and try again.');
      }
    };

    loadDEM();
  }, [selectedState]);

  const toggleDEM = async () => {
    if (!selectedState) {
      console.error("Please select a state first to load DEM.");
      return;
    }

    if (!demLayerRef.current) {
      // This will trigger the useEffect above to load DEM
      return;
    }

    if (showDEM) {
      mapInstanceRef.current.removeLayer(demLayerRef.current);
    } else {
      // Start DEM loading
      setLoadingState(LOADING_STATES.DEM_LOADING);
      setDemLayerLoading(true);
      setProgress(50);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 50));

      try {
        demLayerRef.current.addTo(mapInstanceRef.current);

        // Simulate loading completion
        setTimeout(() => {
          setProgress(100);
          setLoadingMessage(getLoadingMessage(LOADING_STATES.DEM_LOADING, 100));

          setTimeout(() => {
            setLoadingState(LOADING_STATES.IDLE);
            setDemLayerLoading(false);
            setProgress(0);
            setLoadingMessage('');
          }, 300);
        }, 500);

      } catch (error) {
        console.error('Error showing DEM layer:', error);
        setLoadingState(LOADING_STATES.IDLE);
        setDemLayerLoading(false);
        setProgress(0);
        setLoadingMessage('');
      }
    }

    setShowDEM(!showDEM);
  };

  // ✅ Function to Show/Hide Khasra (Parcel) Layer
  // ✅ Updated toggleKhasraLayer function with proper cleanup

  const setupKhasraClickHandler = (
    map,
    khasraLayer,
    villageName,
    selectedTehsil
  ) => {
    // ✅ Remove any existing click handlers first
    map.off("click");

    // ✅ Add new click handler
    map.on("click", async (e) => {
      // ✅ REMOVED the showKhasra check - this was causing the issue!
      // The handler should work if the khasraLayer exists
      if (!khasraLayer || !map) {
        return;
      }

      console.log("🖱️ Khasra click detected");

      // 🚀 UI Feedback: Show loading cursor
      const mapContainer = map.getContainer();
      mapContainer.style.cursor = "wait";

      const { lat, lng } = e.latlng;
      const layerName = khasraLayer.wmsParams.layers;

      // ✅ Use the same filter logic as in toggleKhasraLayer
      let cqlFilter = `state='${selectedState}' AND district='${selectedDistrict}' AND tehsil='${selectedTehsil}'`;

      // If village is selected, filter for that village only
      if (villageName) {
        cqlFilter += ` AND village='${villageName}'`;
      }

      // ✅ Use correct point conversion
      const point = map.latLngToContainerPoint(e.latlng);
      const size = map.getSize();

      const wmsUrl =
        `${GEO_SERVER_BASE_URL}/wms?service=WMS&version=1.1.1&request=GetFeatureInfo` +
        `&layers=${layerName}&query_layers=${layerName}` +
        `&info_format=application/json` +
        `&x=${Math.floor(point.x)}&y=${Math.floor(point.y)}` +
        `&height=${size.y}&width=${size.x}` +
        `&srs=EPSG:4326&bbox=${map.getBounds().toBBoxString()}` +
        `&cql_filter=${encodeURIComponent(cqlFilter)}`;

      try {
        const response = await fetch(wmsUrl);
        const data = await response.json();

        if (!data.features || data.features.length === 0) {
          console.warn("❌ No feature returned from WMS click.");
          mapContainer.style.cursor = ""; // Reset cursor
          return;
        }

        const feature = data.features[0];
        const props = feature.properties;

        const tableFields = [
          { name: "clicked_lat", header: "Latitude" },
          { name: "clicked_lng", header: "Longitude" },
          { name: "state", header: "State" },
          { name: "district", header: "District" },
          { name: "tehsil", header: "Tehsil" },
          { name: "village", header: "Village" },
          { name: "khasra_no", header: "Khasra No" },
          { name: "area_ac", header: "Area (Ac)" },
          { name: "lu_lc", header: "LU/LC" },
        ];

        let tableHtml = `<table class="popup-table"><thead></thead><tbody>`;
        tableFields.forEach((f) => {
          let value =
            f.name === "clicked_lat"
              ? lat.toFixed(6)
              : f.name === "clicked_lng"
                ? lng.toFixed(6)
                : props[f.name] ?? "N/A";

          tableHtml += `<tr><td><strong>${f.header}</strong></td><td>${value}</td></tr>`;
        });

        // ✅ Add data source attribution to popup
        tableHtml += `<tr><td colspan="2" style="text-align: center; font-style: italic; color: #666; padding-top: 8px; border-top: 1px solid #ddd;">
          <small>Source: Land Revenue Department</small>
        </td></tr>`;

        tableHtml += `</tbody></table>`;

        // ✅ Remove previous highlight before adding new one
        if (khasraHighlightRef.current) {
          map.removeLayer(khasraHighlightRef.current);
          khasraHighlightRef.current = null;
        }

        const highlight = L.geoJSON(feature.geometry, {
          style: {
            color: "red", // ✅ Match CHMPage: Red highlight
            weight: 2,
            fillOpacity: 0.2, // ✅ Match CHMPage: 0.2 opacity
          },
          onEachFeature: (f, layer) => {
            layer.bindPopup(tableHtml).openPopup();
          },
        }).addTo(map);

        khasraHighlightRef.current = highlight;
        // map.fitBounds(highlight.getBounds()); // Removed per user request to prevent auto-zoom

        console.log("✅ Khasra feature highlighted");

      } catch (err) {
        console.error("Khasra click fetch failed:", err);
      } finally {
        mapContainer.style.cursor = ""; // Reset cursor
      }
    });

    console.log("✅ Khasra click handler setup complete");
  };

  const toggleKhasraLayer = async (
    selectedTehsil,
    selectedState,
    selectedDistrict,
    villageName
  ) => {
    try {
      setLoadingState(LOADING_STATES.KHASRA_LOADING);
      setProgress(20);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 20));

      if (!selectedTehsil || !selectedState || !selectedDistrict) {
        console.error("Missing input for Khasra layer filter.");
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
        return;
      }

      if (showKhasra && khasraLayerRef.current) {
        // ✅ CLEANUP when hiding Khasra

        // 1. Remove the Khasra layer
        mapInstanceRef.current.removeLayer(khasraLayerRef.current);
        khasraLayerRef.current = null;

        // 2. Remove any existing highlight
        if (khasraHighlightRef.current) {
          mapInstanceRef.current.removeLayer(khasraHighlightRef.current);
          khasraHighlightRef.current = null;
        }

        // 3. Remove the click handler - IMPORTANT!
        mapInstanceRef.current.off("click");

        // 4. Update states
        setShowKhasra(false);
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');

        console.log("✅ Khasra layer hidden and click handler removed");
        return;
      }

      setProgress(40);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 40));

      let khasraLayerName = "";
      if (selectedState === "Rajasthan") {
        khasraLayerName = "ne:Rajasthan_Khasra_All1";
      } else if (selectedState === "Maharashtra") {
        khasraLayerName = "ne:MH_Khasra_All";
      } else if (selectedState === "Uttar Pradesh") {
        khasraLayerName = "ne:UP_Khasra_All";
      } else if (selectedState === "Andhra Pradesh") {
        khasraLayerName = "ne:AP_Khasra_All";
      } else if (selectedState === "Karnataka") {
        khasraLayerName = "ne:Karnataka_KHASRA_ALL";
      } else {
        alert("No Khasra layer configured for this state.");
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');
        return;
      }

      setProgress(60);
      setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 60));

      // ✅ FIXED: Correct CQL filter logic
      let cqlFilter = `state='${selectedState}' AND district='${selectedDistrict}' AND tehsil='${selectedTehsil}'`;

      // If village is selected, filter for that village only
      if (villageName) {
        cqlFilter += ` AND village='${villageName}'`;
        console.log(`🎯 Village-level Khasra filter: ${cqlFilter}`);
      } else {
        // If no village selected, show all Khasra for the tehsil
        console.log(`🏘️ Tehsil-level Khasra filter: ${cqlFilter}`);
      }

      if (!mapInstanceRef.current.getPane("khasraPane")) {
        mapInstanceRef.current.createPane("khasraPane");
        mapInstanceRef.current.getPane("khasraPane").style.zIndex = 550;
      }

      const khasraLayer = L.tileLayer.wms(
        `${GEO_SERVER_BASE_URL}/wms`,
        {
          layers: khasraLayerName,
          format: "image/png",
          transparent: true,
          version: "1.1.0",
          attribution: "Land Revenue Department",
          pane: "khasraPane",
          zIndex: 550,
          CQL_FILTER: cqlFilter,
          tiled: true,
          tiled: true,
          // minZoom: 15, // 🚀 Removed minZoom to match CHMPage behavior
        }
      );

      // Add loading event handlers for Khasra
      khasraLayer.on('loading', () => {
        setProgress(80);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 80));
      });

      khasraLayer.on('load', () => {
        setProgress(100);
        setLoadingMessage(getLoadingMessage(LOADING_STATES.KHASRA_LOADING, 100));

        setTimeout(() => {
          setLoadingState(LOADING_STATES.IDLE);
          setProgress(0);
          setLoadingMessage('');
        }, 400);
      });

      khasraLayer.on('tileerror', (error) => {
        console.error('Khasra layer loading error:', error);
        setLoadingState(LOADING_STATES.IDLE);
        setProgress(0);
        setLoadingMessage('');

        if (!hasShownKhasraTileErrorRef.current) {
          alert('Failed to load Khasra layer. Please try again.');
          hasShownKhasraTileErrorRef.current = true;

          setTimeout(() => {
            hasShownKhasraTileErrorRef.current = false;
          }, 10000); // Reset after 10 seconds
        }
      });


      khasraLayer.addTo(mapInstanceRef.current);
      khasraLayerRef.current = khasraLayer;

      // ✅ Auto-zoom removed to match CHMPage
      // if (mapInstanceRef.current.getZoom() < 15) { ... }

      // ✅ Set showKhasra to true BEFORE setting up click handler
      setShowKhasra(true);

      // ✅ Setup click handler AFTER setting state
      setupKhasraClickHandler(
        mapInstanceRef.current,
        khasraLayer,
        villageName,
        selectedTehsil
      );

      console.log("✅ Khasra layer shown and click handler activated");
      setTimeout(() => ensureKhasraOnTop(), 100);

    } catch (err) {
      console.error("Failed to toggle Khasra:", err);
      setLoadingState(LOADING_STATES.IDLE);
      setProgress(0);
      setLoadingMessage('');
    }
  };

  const handleKhasraButtonClick = () => {
    // ✅ Robustness Fix: Use selectedVillage from Redux/Props directly
    // This avoids issues where extracting from the layer fails (e.g. incorrect property keys)
    const villageName = selectedVillage;

    console.log('🔍 Khasra for village:', villageName);

    toggleKhasraLayer(
      selectedTehsil,
      selectedState,
      selectedDistrict,
      villageName
    );
  };

  const ensureKhasraOnTop = () => {
    if (khasraLayerRef.current && showKhasra) {
      // Make sure our custom pane exists and has the highest z-index
      if (!mapInstanceRef.current.getPane("khasraPane")) {
        mapInstanceRef.current.createPane("khasraPane");
        mapInstanceRef.current.getPane("khasraPane").style.zIndex = 550;
      }

      // Force the khasra layer to redraw in the highest pane
      mapInstanceRef.current.getPane("khasraPane").style.zIndex = 550;

      // If we have LULC layer, make sure it goes below khasra
      if (lulcLayerRef.current && showLULC) {
        lulcLayerRef.current.setZIndex(500);
      }
    }
  };

  // Check if any loading is active
  const isLoading = loadingState !== LOADING_STATES.IDLE;

  return (
    <div className="page-container">
      {/* ✅ Header Section */}
      <div className="header">
        <h3>Elevation Model</h3>
      </div>

      {/* ✅ Map Container */}
      <div style={{ flex: 1, position: "relative", height: "100%" }}>
        <div className="control-panel">
          <SelectionDropdown onSearch={handleSearch} />

          <div className="button-container">
            <button
              onClick={handleKhasraButtonClick} // ✅ Use the new handler instead of inline function
              className={`modern-btn ${showKhasra ? 'toggle-active' : 'toggle-inactive'}`}
              disabled={!selectedState || !selectedTehsil || loadingState === LOADING_STATES.KHASRA_LOADING}
            >
              {loadingState === LOADING_STATES.KHASRA_LOADING
                ? "Loading..."
                : showKhasra
                  ? "Hide Khasra"
                  : "Show Khasra"
              }
            </button>

            <button
              onClick={() => {
                if (satelliteLayerRef.current) {
                  if (isSatelliteVisible) {
                    satelliteLayerRef.current.setOpacity(0);
                  } else {
                    satelliteLayerRef.current.setOpacity(1);
                  }
                  setIsSatelliteVisible(!isSatelliteVisible);
                }
              }}
              className={`modern-btn ${isSatelliteVisible ? 'toggle-active' : 'toggle-inactive'}`}
              disabled={isLoading}
            >
              {isSatelliteVisible ? "Hide Satellite" : "Show Satellite"}
            </button>

            <button
              onClick={toggleDEM}
              className={`modern-btn ${showDEM ? 'toggle-active' : 'toggle-inactive'}`}
              disabled={demLayerLoading || loadingState === LOADING_STATES.DEM_LOADING}
            >
              {loadingState === LOADING_STATES.DEM_LOADING
                ? "Loading..."
                : showDEM
                  ? "Hide DEM"
                  : "Show DEM"
              }
            </button>

            <button
              onClick={toggleLULCLayer}
              className={`modern-btn lulc-btn ${showLULC ? 'active' : ''}`}
              disabled={!selectedTehsilForLULC || lulcLayerLoading || loadingState === LOADING_STATES.LULC_LOADING}
            >
              {loadingState === LOADING_STATES.LULC_LOADING
                ? "Loading..."
                : showLULC
                  ? "Hide LU/LC"
                  : "Show LU/LC"
              }
            </button>

            <button
              onClick={handleHideLayers}
              className={`modern-btn hide-layers-btn ${areLayersHidden ? 'active' : ''}`}
              disabled={isLoading}
            >
              {areLayersHidden ? "Show Layers" : "Hide Layers"}
            </button>
          </div>
        </div>
        <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      </div>

      {/* Loading States */}
      {isLoading && (
        <>
          <Spinner />
          <ProgressBar
            loading={isLoading}
            progress={progress}
            message={loadingMessage}
          />
        </>
      )}
    </div>
  );
};

export default DigitalElevationView;
